import { WallComponent } from './WallElement'
import { RoomComponent } from './RoomElement'
import { BeamComponent } from './BeamElement'
import { JoistComponent } from './JoistElement'
import { PostComponent } from './PostElement'
import { WindowComponent } from './WindowElement'
import { HeaderComponent } from './HeaderElement'
import { DoorComponent } from './DoorElement'
import {
  BaseElementBounds,
  BaseElementData,
} from '../../../models/Construction'

export enum ELEMENT_TYPES {
  WALL = 'wall',
  ROOM = 'room',
  BEAM = 'beam',
  WINDOW = 'window',
  DOOR = 'door',
  COLUMN = 'column',
  STAIR = 'stair',
  OPENING = 'opening',
}

export interface BaseElementProperties {
  material?: string
  height?: number
  name?: string
  area?: number
  usage?: string
  load_capacity?: number
  type?: string
  glazing?: string
  opening_direction?: string
  [key: string]: any
}

export interface BaseElement {
  id: string
  type: ELEMENT_TYPES
  center_point: [number, number]
  angle?: number
  properties?: BaseElementProperties
}

export interface RectangularElement extends BaseElement {
  length: number
  thickness: number
}

export interface CircularElement extends BaseElement {
  radius: number
}

export type Element = RectangularElement | CircularElement

export interface BuildingData {
  walls?: RectangularElement[]
  rooms?: RectangularElement[]
  beams?: RectangularElement[]
  windows?: RectangularElement[]
  doors?: RectangularElement[]
  columns?: CircularElement[]
  stairs?: RectangularElement[]
  openings?: RectangularElement[]
  [key: string]: Element[] | undefined
}

interface Point2D {
  x: number
  y: number
}

interface Point3D extends Point2D {
  z: number
}

interface Coordinate2D extends Array<number> {
  0: number
  1: number
}

interface Coordinate3D extends Array<number> {
  0: number
  1: number
  2: number
}

export interface ElementInteractionCallbacks<T extends BaseElementData> {
  onSelect?: (element: T) => void
  onDelete?: (elementId: string, elementType: string) => void
  onUpdate?: (
    elementId: string,
    elementType: string,
    updates: Partial<T>,
  ) => void
  onResizeStart?: (
    elementId: string,
    elementType: string,
    mode: ResizeMode,
    startPos: Point2D,
    bounds: BaseElementBounds,
  ) => void
}

// Enhanced resize modes for all directions
type ResizeMode =
  | 'none'
  | 'move'
  | 'nw'
  | 'n'
  | 'ne'
  | 'e'
  | 'se'
  | 's'
  | 'sw'
  | 'w'

// Enhanced resize state
interface ResizeState {
  elementId: string
  elementType: string
  mode: ResizeMode
  startPos: Point2D
  startElementState: any
  startBounds: {
    centerX: number
    centerY: number
    width: number
    height: number
    angle: number
  }
}
export const FLOOR_PLAN_COLORS = {
  WALLS: '#D9D9D9',
  BEAMS: '#9E9E9E',
  JOISTS: '#5D5D5D',
  POSTS: '#696969',
  STUDS: '#A9A9A9',
  HEADERS: '#808080',
  WINDOWS: {
    BAY: '#9B96EA',
    LARGE: '#D9D9D9',
    PICTURE: '#9B96EA',
    SLIDING: '#9B96EA',
    SMALL: '#9B96EA',
    HOPPER: '#9B96EA',
    UNKNOWN: '#000000',
  },
  DOORS: '#7C7C7C',
  ROOMS: {
    LIVING_ROOM: '#F0F8FF',
    CLOSET: '#FFF8DC',
    LINEN: '#F5F5DC',
    UNKNOWN: '#F5F5F5',
  },
  TEXT: '#333333',
  BORDER: '#CCCCCC',
  SELECTED: '#9B96EA',
  HOVER: '#FFE66D',
} as const

export type {
  BaseElementData,
  Point2D,
  Point3D,
  Coordinate2D,
  Coordinate3D,
  ResizeState,
  ResizeMode,
}

export {
  WallComponent,
  BeamComponent,
  JoistComponent,
  HeaderComponent,
  PostComponent,
  RoomComponent,
  WindowComponent,
  DoorComponent,
}
